import userDetail from "Stores/userDetail";
import { linkedInCookiesState } from "Stores/LinkedInCookies";
import { linkedInReplyState } from "../state";
import PaywallUtils from "Utils/PaywallUtils";
import { makeApi, URLS } from "Utils/apiURL";
import { fetchLinkedInAccounts } from "Utils/commonAPI";
import { pluginNotInstalled } from "../popup/PluginNotInstalled";
import { listenForResult } from "Pages/Dashboard/connectApps/oauthPopupBridge";
import { reconnectLiSequencePopupState } from "../ReconnectLiSequencePopupState";

const linkedInUtils = {};

const POPUP_POLL_INTERVAL_MS = 1000;
const POPUP_POLL_TIMEOUT_MS = 5 * 60 * 1000;

const STATUS_POLL_INTERVAL_MS = 5000;
const STATUS_POLL_MAX_ATTEMPTS = 10;
const TERMINAL_STATUSES = ["CONNECTED", "FAILED", "DUPLICATE"];

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

const EXTENSION_POLL_INTERVAL_MS = 1500;
const EXTENSION_POLL_MAX_ATTEMPTS = 6; // ~9s ceiling

const requestExtensionSessionData = isSalesNavigator => {
  window.parent.postMessage(
    { data: "message", type: "LINKEDIN_COOKIES_AT" },
    "*"
  );
  window.parent.postMessage(
    { data: "message", type: "LINKEDIN_PROFILE_DATA" },
    "*"
  );
  if (isSalesNavigator) {
    window.parent.postMessage(
      { data: "message", type: "LINKEDIN_COOKIES_A" },
      "*"
    );
  }
};

const pollExtensionForSession = async isSalesNavigator => {
  for (let attempt = 0; attempt < EXTENSION_POLL_MAX_ATTEMPTS; attempt++) {
    requestExtensionSessionData(isSalesNavigator);
    await wait(EXTENSION_POLL_INTERVAL_MS);
    const hasSession = isSalesNavigator
      ? linkedInCookiesState?.cookieLiAt && linkedInCookiesState?.cookieLiA
      : linkedInCookiesState?.cookieLiAt;
    if (hasSession) {
      return true;
    }
  }
  return false;
};

linkedInUtils.connectViaExtension = async (isSalesNavigator = false, isReconnect = false) => {
  const {
    planName = "",
    inFreePlan = false,
    liAutomationEnabled = false,
    noOfLiAccountsUsed = 0,
    noOfLiAccountsEligible = 0
  } = userDetail?.userFeatureFlag || {};

  await linkedInReplyState.getCurrentTeam();
  if (!userDetail?.userFeatureFlag?.liAutomationEnabled) {
    PaywallUtils.upgradeUtilsFunction("linkedInAutomationNotEnabled", false);
    return;
  }
  if (!liAutomationEnabled) {
    return;
  }
  if (!isReconnect && noOfLiAccountsUsed >= noOfLiAccountsEligible) {
    PaywallUtils.upgradeUtilsFunction(
      noOfLiAccountsEligible === 0 || !liAutomationEnabled
        ? "linkedInAutomationNotEnabled"
        : "linkedInAccountLimitReached",
      false,
      planName,
      inFreePlan
    );
    return;
  }

  if (!userDetail?.linkedInConnectpluginInstalled) {
    pluginNotInstalled();
    return;
  }

  // Clear any session data left over from a previous attempt so a late
  // response can't be mistaken for this attempt's result.
  linkedInCookiesState.setCookieLiAt("");
  linkedInCookiesState.setCookieLiA("");
  linkedInCookiesState.setProfileData(null);
  linkedInCookiesState.setIsPremiumAccount(false);

  window.parent.postMessage(
    {
      reload: true,
      type: isSalesNavigator
        ? "SALES_NAVIGATOR_RELOAD_PLUGIN"
        : "RELOAD_PLUGIN"
    },
    "*"
  );
  linkedInReplyState.setLinkedInConnectionLoading(true);

  const found = await pollExtensionForSession(isSalesNavigator);

  window.parent.postMessage({ reload: true, type: "CLOSE_LINKEDIN" }, "*");
  linkedInReplyState.setLinkedInConnectionLoading(false);

  if (found) {
    linkedInReplyState.setShowLinkedInDetailsPopup(true);
  } else {
    linkedInReplyState.setLinkedInMsg(
      "We couldn't detect an active LinkedIn session in this browser. Sign in to LinkedIn, then try again."
    );
    linkedInReplyState.setShowLinkedInNotConnectedPopup(true);
  }
};

const shownTerminalRequestIds = new Set();

const pollOauthConnectStatus = async (liAccountId = null, accountInfo = null) => {
  linkedInReplyState.setOauthConnectStatus("TO_BE_CONNECTED");
  for (let attempt = 0; attempt < STATUS_POLL_MAX_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      await wait(STATUS_POLL_INTERVAL_MS);
    }
    const req = await linkedInReplyState.getOauthConnectRequest();
    const status = req?.status;
    if (TERMINAL_STATUSES.includes(status)) {
      if (req?.id) {
        shownTerminalRequestIds.add(req.id);
      }
      linkedInReplyState.setOauthConnectStatus(status);
      if (status === "CONNECTED") {
        linkedInReplyState.getLinkedInAccounts();
        fetchLinkedInAccounts();
        const resumeLiAccount = req?.linkedinAccount;
        const resolvedLiAccountId = liAccountId || resumeLiAccount?.id;
        const resolvedAccountInfo = liAccountId ? accountInfo : resumeLiAccount;
        if (resolvedLiAccountId) {
          reconnectLiSequencePopupState.checkAndShowPopup(resolvedLiAccountId, resolvedAccountInfo);
        }
      }
      return;
    }
  }
};

const openOAuthPopupAndAwaitClose = popup => {
  return new Promise(resolve => {
    if (!popup) {
      resolve({ completed: false });
      return;
    }

    let settled = false;
    // `completed` distinguishes "the popup actually round-tripped through
    // Unipile/LinkedIn and landed back on our app" from "it was just closed
    // (timeout or the user backing out early)" - only the former means an
    // attempt actually happened and is worth checking status for.
    const finish = completed => {
      if (settled) return;
      settled = true;
      clearInterval(intervalId);
      stopListening();
      if (!popup.closed) {
        popup.close();
      }
      resolve({ completed });
    };

    const stopListening = listenForResult({
      types: ["LINKEDIN_OAUTH_POPUP_DONE"],
      onResult: () => finish(true)
    });

    const startedAt = Date.now();
    const intervalId = setInterval(() => {
      const timedOut = Date.now() - startedAt > POPUP_POLL_TIMEOUT_MS;
      if (popup.closed || timedOut) {
        finish(false);
      }
    }, POPUP_POLL_INTERVAL_MS);
  });
};

linkedInUtils.connectViaOAuth = async (accountId = null, liAccountId = null, accountInfo = null) => {
  const {
    planName = "",
    inFreePlan = false,
    liAutomationEnabled = false,
    noOfLiAccountsUsed = 0,
    noOfLiAccountsEligible = 0
  } = userDetail?.userFeatureFlag || {};

  await linkedInReplyState.getCurrentTeam();
  if (!userDetail?.userFeatureFlag?.liAutomationEnabled) {
    PaywallUtils.upgradeUtilsFunction("linkedInAutomationNotEnabled", false);
    return;
  }
  if (!liAutomationEnabled) {
    return;
  }
  if (!accountId && noOfLiAccountsUsed >= noOfLiAccountsEligible) {
    PaywallUtils.upgradeUtilsFunction(
      noOfLiAccountsEligible === 0 || !liAutomationEnabled
        ? "linkedInAutomationNotEnabled"
        : "linkedInAccountLimitReached",
      false,
      planName,
      inFreePlan
    );
    return;
  }

  try {
    const config = accountId
      ? {
          url: `${URLS.getLinkedInOAuthReconnectUrl}?accountId=${accountId}`,
          method: "GET"
        }
      : { url: URLS.getLinkedInOAuthConnectUrl, method: "GET" };
    const res = await makeApi(config);
    const authUrl = res?.data?.authUrl || res?.authUrl;
    if (!authUrl) {
      linkedInReplyState.setLinkedInMsg(
        "Unable to start the LinkedIn connection. Please try again."
      );
      linkedInReplyState.setShowLinkedInNotConnectedPopup(true);
      return;
    }
    const popup = window.open(
      authUrl,
      "linkedInOAuthConnect",
      "width=500,height=700"
    );
    const { completed } = await openOAuthPopupAndAwaitClose(popup);
    if (completed) {
      pollOauthConnectStatus(liAccountId, accountInfo);
    }
  } catch (e) {
    linkedInReplyState.setLinkedInMsg(
      "Something went wrong while connecting your LinkedIn account."
    );
    linkedInReplyState.setShowLinkedInNotConnectedPopup(true);
  }
};

const RECENT_TERMINAL_RESULT_WINDOW_MS = 2 * 60 * 1000;

linkedInUtils.resumeOauthConnectStatusIfNeeded = async () => {
  const req = await linkedInReplyState.getOauthConnectRequest();
  const status = req?.status;
  if (!status) {
    return;
  }

  if (status === "TO_BE_CONNECTED") {
    const notExpired = !req.expiry || new Date(req.expiry).getTime() > Date.now();
    if (notExpired) {
      pollOauthConnectStatus();
    }
    return;
  }

  if (TERMINAL_STATUSES.includes(status)) {
    if (req.id && shownTerminalRequestIds.has(req.id)) {
      return;
    }
    const resolvedAt = req.updateTs ? new Date(req.updateTs).getTime() : 0;
    const isRecent = Date.now() - resolvedAt < RECENT_TERMINAL_RESULT_WINDOW_MS;
    if (isRecent) {
      if (req.id) {
        shownTerminalRequestIds.add(req.id);
      }
      linkedInReplyState.setOauthConnectStatus(status);
      if (status === "CONNECTED") {
        linkedInReplyState.getLinkedInAccounts();
        fetchLinkedInAccounts();
        if (req?.linkedinAccount?.id) {
          reconnectLiSequencePopupState.checkAndShowPopup(req.linkedinAccount.id, req.linkedinAccount);
        }
      }
    }
  }
};

linkedInUtils.markOAuthConnectionStarted = async accountId => {
  if (!accountId) return;
  try {
    await makeApi({
      url: `${URLS.markLinkedInOAuthConnectStartedUrl}?accountId=${accountId}`,
      method: "PUT"
    });
  } catch (e) {
    console.error("[LinkedIn OAuth] markOAuthConnectionStarted failed:", e);
  }
};

linkedInUtils.handleSalesNavigatorButtonClick = async () => {
  await linkedInUtils.connectViaOAuth();
};

linkedInUtils.handleButtonClick = async () => {
  await linkedInUtils.connectViaOAuth();
};

export { linkedInUtils };
export default linkedInUtils;
